// App-wide providers. Mirrors apps/dashboard/src/lib/providers.tsx but
// points the API client at /admin/v1/* and exposes the staff identity
// (loaded from GET /admin/v1/me) via context so the sidebar nav can
// scope-gate links and the topbar can render the user button.

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { type ApiClient, createApiClient } from './api-client.js';
import { env } from '../env.js';
import { ThemeProvider } from './theme.js';
import { SidebarStateProvider } from './sidebar-state.js';
import { TooltipProvider } from '../components/ui/tooltip.js';
import { Toaster } from '../components/ui/toaster.js';

export const ApiClientContext = createContext<ApiClient | null>(null);

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) throw new Error('useApiClient must be used inside <AppProviders>');
  return client;
}

// Subset of StaffScope from @cortex/db, duplicated client-side so the
// admin SPA doesn't pull in the server package. Keep in sync — the
// shared package would be the right home once a third app needs them.
export type StaffScope =
  | 'staff:read'
  | 'staff:support'
  | 'staff:finance'
  | 'staff:platform';

export interface StaffMe {
  staffId: string;
  email: string;
  scopes: readonly StaffScope[];
  mfaVerified: boolean;
}

const StaffMeContext = createContext<StaffMe | null>(null);

export function useStaffMe(): StaffMe {
  const me = useContext(StaffMeContext);
  if (!me) throw new Error('useStaffMe must be used inside <AppProviders>');
  return me;
}

export function useHasStaffScope(scope: StaffScope): boolean {
  const me = useStaffMe();
  return me.scopes.includes(scope);
}

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          const status = (error as { status?: number }).status;
          if (status === 401 || status === 403) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();

  const apiClient = useMemo(
    () =>
      createApiClient({
        // The admin SPA always speaks /admin/v1/*. Keeping the prefix in
        // the client (rather than at every call-site) means feature work
        // calls just `client.get('/me')` and stays focused on shape.
        baseUrl: `${env.apiBaseUrl}/admin/v1`,
        getToken: () => getToken(),
      }),
    [getToken],
  );

  const queryClient = useMemo(makeQueryClient, []);

  return (
    <ThemeProvider>
      <SidebarStateProvider>
        <QueryClientProvider client={queryClient}>
          <ApiClientContext.Provider value={apiClient}>
            <TooltipProvider delayDuration={200}>
              <StaffIdentityGate>{children}</StaffIdentityGate>
              <Toaster />
            </TooltipProvider>
          </ApiClientContext.Provider>
        </QueryClientProvider>
      </SidebarStateProvider>
    </ThemeProvider>
  );
}

// Loads GET /admin/v1/me before rendering the shell. Three states:
//   • loading      → blank screen (admin operators are signed-in
//                    Clerk users; this hop is fast)
//   • error        → render a centered error card with the API code
//                    plus a sign-out CTA. 403/not_staff is the common
//                    case (Clerk user without a staff_users row); we
//                    surface it explicitly so the operator knows why.
//   • ok           → expose StaffMe via context and render children.
function StaffIdentityGate({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const query = useQuery<StaffMe>({
    queryKey: ['staff-me'],
    queryFn: () => client.get<StaffMe>('/me'),
    staleTime: 60_000,
  });

  if (query.isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading staff identity…</div>
      </div>
    );
  }
  if (query.isError) {
    // The API uses one error code (`tenant_unauthorized`) for every 403 on
    // the admin surface and disambiguates via `details.reason`. Branch on
    // that here so an MFA-missing operator doesn't see "not staff" copy
    // (the original UX bug — staff_users row exists but Clerk session is
    // sub-MFA).
    const err = query.error as {
      status?: number;
      code?: string;
      message?: string;
      body?: { details?: { reason?: string } };
    };
    const reason = err.body?.details?.reason;
    const isMfa =
      err.status === 403 &&
      err.code === 'tenant_unauthorized' &&
      reason === 'mfa_required';
    const isNotStaff =
      err.status === 403 &&
      err.code === 'tenant_unauthorized' &&
      reason === 'not_staff';
    return (
      <div className="flex h-screen items-center justify-center p-8">
        <div className="max-w-md space-y-3 rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
          <h1 className="text-base font-semibold">
            {isMfa ? 'Two-factor authentication required' : 'Admin access denied'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isMfa ? (
              <>
                The admin requires an MFA-verified Clerk session. Open your
                Clerk account → Security → enable two-step verification, then
                sign out and sign back in. Once your session reports{' '}
                <code>mfa_verified</code>, this page will load.
              </>
            ) : isNotStaff ? (
              'Your Clerk account is signed in but is not registered as a Cortex staff member.'
            ) : (
              err.message ?? 'The admin API rejected this session.'
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            If you believe this is a mistake, ask a staff:platform admin to invite your email.
          </p>
        </div>
      </div>
    );
  }
  return <StaffMeContext.Provider value={query.data}>{children}</StaffMeContext.Provider>;
}
